import { z } from 'zod';

/**
 * Object interaction contracts (spec §6.6): the shared layer every interactive object and
 * mini-game is built on. The client sends `interact { objectId }` (optionally with an action and
 * data) when the player presses E next to an object; the server validates range, cooldown and
 * permission and then runs the object type's behavior.
 */

/** Default reach in tiles (Chebyshev distance to the object's footprint). */
export const DEFAULT_INTERACTION_RANGE = 1;
/** Hard ceiling for any behavior's range: the prompt only looks this far. */
export const MAX_INTERACTION_RANGE = 6;
/** Behaviors may store a small JSON value per object; this caps its serialized size. */
export const OBJECT_DATA_MAX_BYTES = 2_048;
/** A PIN door's code: exactly this many digits (ADR-0039). */
export const PIN_LENGTH = 4;
export const pinSchema = z.string().regex(new RegExp(`^\\d{${PIN_LENGTH}}$`));

/** Longest action name and activity key. */
const KEY_MAX = 48;
/** Upper bound for a free-form payload before the behavior's own schema checks it. */
const INPUT_DATA_MAX_BYTES = 1_024;

/** Keys are short lower-case words: `open`, `spin`, `drink:coffee`. */
export const interactionKeySchema = z
  .string()
  .min(1)
  .max(KEY_MAX)
  .regex(/^[a-z0-9_:-]+$/);

/**
 * Client → server. `action` picks one of several things an object can do (default: its main
 * action); `data` is checked by the behavior's own zod schema.
 */
export const interactMessageSchema = z
  .object({
    objectId: z.number().int().positive(),
    action: interactionKeySchema.optional(),
    data: z
      .unknown()
      .optional()
      .refine((value) => value === undefined || jsonSize(value) <= INPUT_DATA_MAX_BYTES, {
        message: 'data too large',
      }),
  })
  .strict();
export type InteractMessage = z.infer<typeof interactMessageSchema>;

/** Why an interaction was refused. */
export const INTERACTION_REJECTIONS = [
  'invalid',
  'unknown-object',
  'not-interactive',
  'out-of-range',
  'cooldown',
  'forbidden',
  'busy',
  'unavailable',
  /** Nothing left to take (an empty candy bowl). */
  'empty',
  /** A board has no room for more strokes or notes. */
  'full',
] as const;
export type InteractionRejection = (typeof INTERACTION_REJECTIONS)[number];

/** Server → the interacting player: the interaction was refused. */
export interface InteractRejectedEvent {
  readonly objectId: number;
  readonly reason: InteractionRejection;
  /** For `cooldown`: when the object can be used again (server time, ms). */
  readonly retryAt?: number;
}

/**
 * Server → one player or everyone: something happened at an object (a wheel spun, a panel should
 * open). `kind` is defined by the object type's behavior; `data` is plain JSON.
 */
export interface ObjectEvent {
  readonly objectId: number;
  readonly objectType: string;
  readonly kind: string;
  readonly data?: unknown;
  readonly by: string;
  readonly at: number;
}

/**
 * What the client needs to know about an interactive object type: how far the prompt reaches and
 * the i18n key of its label ("E: Open calendar"). The server attaches the behavior itself; an
 * object type is interactive only when both sides know it.
 */
export interface InteractionDescriptor {
  readonly range: number;
  /** i18n key under `interact.*`. */
  readonly labelKey: string;
  /**
   * When an object offers several actions (coffee or tea), pressing E opens a small menu with
   * these instead of sending the default action.
   */
  readonly choices?: readonly InteractionChoice[];
}

/** One entry of an object's E menu: the action to send and its i18n label. */
export interface InteractionChoice {
  readonly action: string;
  readonly labelKey: string;
}

/** Interactive object types. Add an entry here and a behavior on the server to make one usable. */
export const INTERACTIONS: Readonly<Record<string, InteractionDescriptor>> = {
  wall_calendar: { range: 1, labelKey: 'interact.wall_calendar' },
  // Drinks and candy (spec §6.14.3, ADR-0029).
  coffee_machine: {
    range: 1,
    labelKey: 'interact.coffee_machine',
    choices: [
      { action: 'coffee', labelKey: 'interact.drink.coffee' },
      { action: 'tea', labelKey: 'interact.drink.tea' },
    ],
  },
  beer_tap: { range: 1, labelKey: 'interact.beer_tap' },
  fridge: {
    range: 1,
    labelKey: 'interact.fridge',
    choices: [
      { action: 'soda', labelKey: 'interact.drink.soda' },
      { action: 'water', labelKey: 'interact.drink.water' },
    ],
  },
  candy_bowl: { range: 1, labelKey: 'interact.candy_bowl' },
  // Whiteboard and bulletin board (spec §6.15, ADR-0029).
  whiteboard: { range: 2, labelKey: 'interact.whiteboard' },
  bulletin_board: { range: 2, labelKey: 'interact.bulletin_board' },
  dance_floor: { range: 1, labelKey: 'interact.dance_floor' },
  ping_pong_table: { range: 1, labelKey: 'interact.ping_pong_table' },
  cat_spot: { range: 1, labelKey: 'interact.cat_spot' },
  // Moves the player to another part of the map, e.g. the other office (ADR-0037).
  teleporter: { range: 1, labelKey: 'interact.teleporter' },
  // A heavy door that opens while enough players hold it together (ADR-0038).
  coop_door: { range: 1, labelKey: 'interact.coop_door' },
  // A plain door any player opens and closes with E (ADR-0041).
  toggle_door: { range: 1, labelKey: 'interact.toggle_door' },
  // A desk someone can claim or let go of (ADR-0042).
  desk: { range: 1, labelKey: 'interact.desk' },
  // A door locked behind a 4 digit PIN, then opened and closed like a plain door (ADR-0039).
  pin_door: { range: 1, labelKey: 'interact.pin_door' },
};

export function interactionFor(type: string): InteractionDescriptor | undefined {
  return Object.hasOwn(INTERACTIONS, type) ? INTERACTIONS[type] : undefined;
}

/** Serialized JSON size in bytes (UTF-8), or Infinity when the value cannot be serialized. */
export function jsonSize(value: unknown): number {
  try {
    const text = JSON.stringify(value);
    return text === undefined ? 0 : utf8Length(text);
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}

function utf8Length(text: string): number {
  let bytes = 0;
  for (const char of text) {
    const code = char.codePointAt(0) ?? 0;
    bytes += code < 0x80 ? 1 : code < 0x800 ? 2 : code < 0x10000 ? 3 : 4;
  }
  return bytes;
}
